import React, { useState, useEffect, useRef, memo } from 'react';
import { TPhotoSize, TLoaderType } from 'types';
import { useBlobDownload } from 'hooks/useBlobDownload';
import { getFilePathForMediaFilesWithSizeForStyleSettings } from 'backend/additionalInformation';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import breadcrumbs from 'images/breadcrumbs.svg';
// import { createAppError } from 'utils/helpers';
import classNames from 'classnames';
import style from './ImgUploader.module.scss';

interface IProps {
	translationID: string; // if "" will show noImageIcon
	photoSize: TPhotoSize;
	isMakeImageRequest: boolean;
	loaderSize: TLoaderType;
	noImageIdIcon?: string;
	filePath?: Nullable<string>;
	isUseInSlider?: boolean;
}

export const ImgUploaderForStyleSettings: React.FC<IProps> = memo(
	({
		translationID,
		photoSize,
		isMakeImageRequest,
		loaderSize,
		noImageIdIcon = 'menu',
		filePath = null,
		isUseInSlider = false,
	}) => {
		const [isShowImage, setIsShowImage] = useState<boolean>(false);
		const [isLoading, setIsLoading] = useState<boolean>(true);
		const [actualPath, setActualPath] = useState<string>('');
		const componentMounted = useRef(true);
		const blobDownload = useBlobDownload({
			filePath,
			fileExtension: filePath?.split('.').pop() === 'gif' ? 'gif' : 'png',
			fileName: 'styleImage',
		});
		const containerClasses = classNames(style.container, isUseInSlider ? style.inSlider : style.notInSlider);

		const showError = () => {
			setActualPath('error');
			setIsLoading(false);
		};

		const initialization = async () => {
			try {
				const response = await getFilePathForMediaFilesWithSizeForStyleSettings(translationID, photoSize);
				if (componentMounted.current) {
					setActualPath(response.data.preSignedUrl ? response.data.preSignedUrl : '');
				}
			} catch (err) {
				if (componentMounted.current) {
					showError();
					// const error = err as Error;
					// const appError = createAppError(error);
					// addAppError(appError);
				}
			}
		};

		useEffect(() => {
			if (isShowImage) {
				translationID !== '' ? initialization() : setIsLoading(false);
			}
		}, [isShowImage]);

		// ----- This is to avoid unnecessary photo requests ----- //
		useEffect(() => {
			if (!isShowImage && isMakeImageRequest) {
				setIsShowImage(true);
			}
		}, [isMakeImageRequest]);
		// ----- This is to avoid unnecessary photo requests ----- //

		useEffect(() => {
			return () => {
				componentMounted.current = false;
			};
		}, []);

		return (
			<div className={containerClasses} data-test="ImgUploaderForStyleSettings">
				{isLoading ? <LoaderWithProps loaderSize={loaderSize} /> : null}
				{actualPath !== 'error' && actualPath && (
					<div className={style.imgWrapper}>
						{filePath && (
							<div onClick={blobDownload} className={style.downloadOverlay}>
								<svg className={style.downloadIcon}>
									<use xlinkHref={`${breadcrumbs}#downloadArrow`} />
								</svg>
							</div>
						)}
						<img
							className={style.img}
							src={actualPath}
							onLoad={() => setIsLoading(false)}
							onError={showError}
						/>
					</div>
				)}
				{actualPath === '' && (
					<div className={style.noImage}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#${noImageIdIcon}`} />
						</svg>
					</div>
				)}
				{actualPath === 'error' && (
					<div className={style.noImage}>
						<div className={style.flexColumn}>
							<svg className={style.iconError}>
								<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
							</svg>
							<div>Error image</div>
						</div>
					</div>
				)}
			</div>
		);
	},
);
